/**
 * Application run entry point
 */

import moment from 'moment'
import { PREFERENCES_DEFAULTS, YM_COUNTER } from 'app/core/constants'

/**
 * runMwApp run entry point
 *
 * @class runMwApp
 * @memberof mwApp
 * @see mwApp
 * @listens mwApp.event:loginRequired
 *
 * @ngdoc run
 * @name mwApp.run:runMwApp
 */
/* @ngInject */
export default function runMwApp (
  $rootScope, $window, $state, $transitions, $uibModal, $http, $parse, $trace,
  e401Svc, notifySvc, authSvc, breadcrumbsSvc, storageSvc, $location, historySvc, keycloakConfigCheckSvc
) {
  if ($location.path() === '/login') {
    storageSvc.local.remove('userType')
  }
  // init Yandex.Metrika counter with condition
  function loadYM (m, e, t, r, i, k, a) {
    m[i] = m[i] || function () { (m[i].a = m[i].a || []).push(arguments) }
    m[i].l = 1 * new Date()
    for (let j = 0; j < document.scripts.length; j++) {
      if (document.scripts[j].src === r) { return }
    }
    k = e.createElement(t)
    a = e.getElementsByTagName(t)[0]
    k.async = 1
    k.src = r
    a.parentNode.insertBefore(k, a)
  }

  const condition = $rootScope.enabledYM = window.ENV.APP_YM_ENABLED
  if (condition) {
    loadYM(window, document, 'script', 'load-metrika/tag.js', 'ym')
    window.ym(YM_COUNTER, 'init', {
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
      webvisor: true
    })
  }
  // end of Yandex.Metrika counter
  /**
   * Init the function that sends session data to third part services
   *
   * @type {(function(): void)|*}
   */
  $rootScope.sendSessionData = authSvc.setSessionData()

  // init auth service
  if (!authSvc.hasKeycloak()) {
    authSvc.init()
  } else {
    // Check if Keycloak configuration has changed BEFORE initializing
    // This check runs here so we know the current page path and userType
    const configChanged = keycloakConfigCheckSvc.checkAndClear($window.ENV, authSvc)
    if (configChanged) {
      // Page will reload, so return early
      // Note: clearAndClear already triggered reload
      return
    }

    // Also check after Keycloak initialization (to save hash if first time)
    $rootScope.$on('event:keycloakInited', function () {
      keycloakConfigCheckSvc.checkAndClear($window.ENV, authSvc)
    })

    const userType = storageSvc.local.get('userType')
    if (!['/login', '/setplex-login'].includes($location.path())) {
      const path = $window.location.pathname.replace(/^\/nora/, '')
      if (path === '/setplex-user-403' || userType === 'SYSTEM') {
        $state.go('setplex-login', { go: path })
        return
      }
      $state.go('login', { go: path })
    }
  }
  // get timezone from storage and set default timezone for moment.js
  moment.tz.setDefault(storageSvc.local.get('timezone'))

  // expose hasAccess method from Auth Service to $rootScope
  // to be able to use it in angular expressions
  $rootScope.hasAccess = authSvc.hasAccess

  // get last crumb, used to change page title
  $rootScope.lastCrumb = breadcrumbsSvc.last

  // field getter for smart table st-multi-sort plugin
  $rootScope._f = field => object => $parse(field)(object) || ''

  // expose 'skin' to global scope
  $rootScope.appSkin = storageSvc.local.get('skin', PREFERENCES_DEFAULTS.skin)
  $rootScope.provider = window.ENV.APP_PROVIDER
  $rootScope.features = storageSvc.local.get('features')
  $rootScope.ageRatings = storageSvc.local.get('ageRatings')
  $rootScope.countries = storageSvc.local.get('countries')

  if (!$rootScope?.appSkin?.sidebar?.includes('ui-dark')) {
    $rootScope.appSkin = { sidebar: 'ui-dark' }
  }

  /**
   * On 'event:loginRequired' show auth form
   */
  var authRequested = false
  $rootScope.$on('event:loginRequired', function () {
    if (!$rootScope.authenticated) {
      e401Svc.rejectAll() // reject posponed requests if not authorized
      $state.go('login')
    } else
    if (!authRequested) {
      authRequested = true
      var modalScope = $rootScope.$new()
      modalScope.modalInstance = $uibModal.open({
        animation: false,
        scope: modalScope,
        backdropClass: 'modal-top', // set z-index to 100500
        windowClass: 'modal-top', // set z-index to 100510
        templateUrl: 'components/auth/views/login.html',
        controller: 'LoginCtrl as vm',
        resolve: {
          $transition$: null // there is no transition for popup window
        },
        size: 'lg',
        backdrop: 'static'
      })
      modalScope.modalInstance.result
        .then(
          function close () {
            e401Svc.retryAll($http) // retry all posponed requests
            authRequested = false
          },
          function dismiss () {
            e401Svc.rejectAll() // reject all postponed requests
            authRequested = false
            $state.go('login')
          }
        )
    }
  })

  if (process.env.NODE_ENV === 'development') {
    $trace.enable('TRANSITION')
  }

  // Redirect to 404 page in case of 404 error during transition (when http resolve is rejected)
  $state.defaultErrorHandler((error) => {
    if (error.detail && error.detail.status === 404) {
      $state.go('app.404', { message: error.detail.data })
    }
  })

  // Revert url, when transition was rejected
  // https://github.com/angular-ui/ui-router/issues/3416
  $transitions.onError({},
    (trans) => {
      const options = trans.options()
      if (options.source === 'url') { // revert the url when the transition was triggered by a URL sync (options.source === 'url')
        const $state = trans.router.stateService
        const $urlRouter = trans.router.urlRouter
        if ($state.$current.navigable) {
          const urlOptions = { replace: true }
          $urlRouter.push($state.$current.navigable.url, $state.params, urlOptions)
          $urlRouter.update(true)
        }
      }
    }
  )

  // Add UI Router `.onBefore` Transition Hook
  // https://ui-router.github.io/guide/ng1/migrate-to-1_0#state-change-events
  // For all states, that has access field
  $transitions.onBefore({ to: (state) => state.self && state.self.access != null },
    (trans) => {
      if (!authSvc.hasAccess(trans.to().access, trans.params('to'))) {
        if ($rootScope.authenticated) {
          // if user is authenticated, but has no access -> abort transition
          trans.abort()
          $state.go('app.403')
        } else {
          // if user is not authenticated -> redirect to `login` state
          const path = $window.location.pathname.replace(/^\/nora/, '')
          return trans.router.stateService.target('login', path && path !== '/' ? { go: path } : undefined)
        }
      }
    }
  )

  $transitions.onSuccess({}, () => {
    historySvc.push({}, '', '/nora' + $location.url())
  })

  /**
   * On 'event:changeSkin' change skin
   */
  $rootScope.$on('event:changeSkin', (e, skin) => {
    $rootScope.appSkin = skin
  })

  /**
   * On change 'skin' in localStorage -> reread it
   */
  storageSvc.local.onChange('skin', function (e) {
    if (e.newValue) {
      $rootScope.appSkin = storageSvc.local.get('skin', PREFERENCES_DEFAULTS.skin)
    }
  })

  /**
   * On change 'authenticated' flag in localStorage -> reload full page
   */
  storageSvc.local.onChange('authenticated', function (e) {
    if (e.newValue === null) {
      $rootScope.authenticated = false
      notifySvc.clear()
    }
    $window.location.reload()
  })

  /**
  * On change 'features' flag in localStorage  is empty-> load flags and reload full page
  */
  storageSvc.local.onChange('features', function (e) {
    // $rootScope.features = storageSvc.local.get('features')
    if (!e.newValue) {
      authSvc.manageFeatureFlags(true)
    }
  })

  /**
  * On change 'currencies' in localStorage  is empty-> load flags and reload full page
  */
  storageSvc.local.onChange('currencies', function (e) {
    if (e.oldValue) {
      authSvc.manageCurrencies(true)
    }
  })

  /**
   * On change 'features' flag in localStorage  is empty-> load flags and reload full page
   */
  storageSvc.local.onChange('user', function (e) {
    // $rootScope.features = storageSvc.local.get('features')
    if (!e.newValue) {
      authSvc.getUser(true)
    }
  })
  /**
   * On change 'features' flag in localStorage  is empty-> load flags and reload full page
   */
  storageSvc.local.onChange('userType', function (e) {
    // $rootScope.features = storageSvc.local.get('features')
    if (!e.newValue) {
      storageSvc.local.set('userType', JSON.parse(storageSvc.local.get('user')).userType)
    }
  })
  /**
  * On change 'ageRatings' in localStorage  is empty-> load flags and reload full page
  */
  storageSvc.local.onChange('ageRatings', function (e) {
    if (!e.newValue) {
      authSvc.manageAgeRatings(true)
    }
  })

  /**
  * On change 'countries' in localStorage  is empty-> load flags and reload full page
  */
  storageSvc.local.onChange('countries', function (e) {
    if (!e.newValue) {
      authSvc.manageAgeRatings(true)
    }
  })

  /**
  * On change 'countries' in localStorage  is empty-> load flags and reload full page
  */
  storageSvc.local.onChange('currencies', function (e) {
    if (!e.newValue) {
      authSvc.manageCurrencies(true)
    }
  })

  /**
   * On change 'KEYCLOAK_TOKEN' flag in localStorage  is empty-> load flags and reload full page
   */
  storageSvc.local.onChange('KEYCLOAK_TOKEN', function (e) {
    // $rootScope.features = storageSvc.local.get('features')
    if (!e.newValue) {
      if (authSvc.hasKeycloak()) {
        authSvc.keycloakInitEventSubscribe(null, false)
      }
    }
  })
}
